import * as yup from 'yup';

import { Button, Input } from '../../../components';
import { Card, CardBody, Col, Form, FormGroup, Label, Row } from 'reactstrap';
import { CoaDto, ProfessionDetail, ProfessionLengthOfInitialDto } from '../../../common/Types';
import React, { useEffect, useState } from 'react';

import Skeleton from 'react-loading-skeleton';
import { coaService } from '../../../services/CoaAdministration';
import { professionService } from '../../../services/ProfessionAdministration';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

interface ProfessionDetailProps {
    detail: ProfessionDetail;
    onUpdate: () => void;
}

const ProfessionDetailCard = (props: ProfessionDetailProps) => {
    const { onUpdate } = props;
    const { professionId, title, isDeleted, armsUser, coaId, lengthOfInitial, showDegreeOnCertificate } = props.detail;
    const [coas, setCoas] = useState<CoaDto[]>();
    const [editing, setEditing] = useState<boolean>(false);
    const [selectedCoa, setSelectedCoa] = useState<CoaDto>();

    const toggleEditing = () => setEditing(!editing);

    const professionSchema = yup
        .object<ProfessionDetail>({
            professionId: yup.number().required().default(professionId),
            title: yup.string().max(1000).required().default(title),
            isDeleted: yup.boolean().required().default(isDeleted),
            armsUser: yup.boolean().required().default(armsUser),
            showDegreeOnCertificate: yup.boolean().required().default(showDegreeOnCertificate),
            coaId: yup.number().notRequired().nullable().typeError('Please select a CoA').default(coaId),
            lengthOfInitial: yup
                .object<ProfessionLengthOfInitialDto>({
                    lengthId: yup
                        .number()
                        .required()
                        .default(lengthOfInitial?.lengthId || 0),
                    professionId: yup.number().required().default(professionId),
                    initialLength: yup
                        .number()
                        .required()
                        .min(1, 'Must be greater than 0')
                        .typeError('Must be greater than 0')
                        .default(lengthOfInitial?.initialLength || 0),
                })
                .default(lengthOfInitial || undefined),
        })
        .defined();

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(professionSchema as any),
        defaultValues: { ...props.detail } as ProfessionDetail,
    });

    useEffect(() => {
        const loadCoas = () => {
            if (!coas) {
                coaService
                    .getCoas()
                    .then((results) => {
                        setCoas([...results.filter((c) => c.isActive)]);
                        if (coaId && coaId > 0) {
                            setSelectedCoa(results.find((r) => r.id === coaId && r.isActive));
                        }
                    })
                    .catch(() => {
                        toast.error('Error loading CoA list');
                    });
            }
        };

        loadCoas();
    }, [coaId, coas, setSelectedCoa]);

    const onSubmit = (values: ProfessionDetail) => {
        const toastId = toast.info('Saving profession detail...');

        professionService
            .saveProfessionDetail(values)
            .then((result) => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Profession detail saved successfully',
                });
                if (coas) {
                    setSelectedCoa(coas.find((r) => r.id === result.coaId && r.isActive));
                }
                reset(result);
                setEditing(false);
                if (onUpdate) {
                    onUpdate();
                }
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'There was an error saving profession detail',
                });
            });
    };

    return (
        <Card>
            <CardBody>
                {coas && editing && (
                    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                        <FormGroup>
                            <Label>{`Title`}</Label>
                            <Controller
                                name="title"
                                control={control}
                                render={({ field }) => <Input type="text" {...field} value={field.value ?? ''} />}
                            />
                            {errors?.title?.message && (
                                <span className={'text-danger'}>{errors.title.message}</span>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label>{`CoA`}</Label>
                            <Controller
                                name="coaId"
                                control={control}
                                render={({ field }) => (
                                    <Input type="select" {...field} value={field.value ?? ''}>
                                        <option value={``}>{`Select a CoA`}</option>
                                        {coas.map((coa, i) => (
                                            <option key={i} value={coa.id}>
                                                {coa.coaTitle}
                                            </option>
                                        ))}
                                    </Input>
                                )}
                            />
                            {errors?.coaId?.message && (
                                <div>
                                    <span className={'text-danger'}>{errors.coaId.message}</span>
                                </div>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label>{`Length of Initial`}</Label>
                            <Controller
                                name="lengthOfInitial.initialLength"
                                control={control}
                                render={({ field }) => <Input type="number" {...field} value={field.value ?? ''} />}
                            />
                            {errors?.lengthOfInitial && errors.lengthOfInitial['initialLength']?.message && (
                                <div>
                                    <span className={'text-danger'}>
                                        {errors.lengthOfInitial['initialLength'].message}
                                    </span>
                                </div>
                            )}
                        </FormGroup>
                        <div className="custom-control custom-switch">
                            <Controller
                                name="armsUser"
                                control={control}
                                render={({ field: { value, onChange, onBlur, name, ref } }) => (
                                    <input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id="armsUser"
                                        name={name}
                                        ref={ref}
                                        checked={value ?? false}
                                        onChange={(e) => onChange(e.target.checked)}
                                        onBlur={onBlur}
                                    />
                                )}
                            />
                            <Label className="custom-control-label" for="armsUser">
                                {`Is Using ARMS?`}
                            </Label>
                        </div>
                        <div className="custom-control custom-switch mt-3">
                            <Controller
                                name="showDegreeOnCertificate"
                                control={control}
                                render={({ field: { value, onChange, onBlur, name, ref } }) => (
                                    <input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id="showDegreeOnCertificate"
                                        name={name}
                                        ref={ref}
                                        checked={value ?? false}
                                        onChange={(e) => onChange(e.target.checked)}
                                        onBlur={onBlur}
                                    />
                                )}
                            />
                            <Label className="custom-control-label" for="showDegreeOnCertificate">
                                {`Show Degree on Certificate?`}
                            </Label>
                        </div>
                        <Row className={`mt-3`}>
                            <Button type={`submit`} color={'primary'} className={'mr-2'}>{`Save`}</Button>
                            <Button
                                type={`button`}
                                color={'secondary'}
                                outline
                                onClick={() => toggleEditing()}
                            >{`Cancel`}</Button>
                        </Row>
                    </Form>
                )}
                {coas && !editing && (
                    <>
                        <Row>
                            <Col xs={12} className={`d-flex flex-column`}>
                                <Label>{`Title`}</Label>
                                <span>{title}</span>
                            </Col>
                            <Col xs={12} className={`d-flex flex-column mt-2`}>
                                <Label>{`CoA`}</Label>
                                {selectedCoa && <span>{selectedCoa.coaTitle}</span>}
                                {!selectedCoa && <span>{`Not Selected`}</span>}
                            </Col>
                            {selectedCoa && (
                                <Col xs={12} className={`d-flex flex-column mt-2`}>
                                    <Label>{`Chair`}</Label>
                                    {selectedCoa && <span>{selectedCoa.chairFullName}</span>}
                                    {!selectedCoa && <span>{`Not Selected`}</span>}
                                </Col>
                            )}
                            <Col xs={12} className={`d-flex flex-column mt-2`}>
                                <Label>{`Length of Initial`}</Label>
                                {lengthOfInitial && <span>{lengthOfInitial.initialLength}</span>}
                                {!lengthOfInitial && <span>{`Not Set`}</span>}
                            </Col>
                            <Col xs={12} className={`d-flex flex-column mt-2`}>
                                <Label>{`Is Using ARMS?`}</Label>
                                <span>{armsUser ? 'Yes' : 'No'}</span>
                            </Col>
                            <Col xs={12} className={`d-flex flex-column mt-2`}>
                                <Label>{`Show Degree on Certificate?`}</Label>
                                <span>{showDegreeOnCertificate ? 'Yes' : 'No'}</span>
                            </Col>
                        </Row>
                        <Row className={`mt-3`}>
                            <Col>
                                <Button
                                    type={`button`}
                                    color={`primary`}
                                    onClick={() => toggleEditing()}
                                >{`Edit`}</Button>
                            </Col>
                        </Row>
                    </>
                )}
                {!coas && <Skeleton count={10} />}
            </CardBody>
        </Card>
    );
};

export default ProfessionDetailCard;
